import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { FC, useEffect, useState } from "react";
import { DateRangePicker, createStaticRanges } from "react-date-range";
import {
  format,
  addDays,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addMonths,
} from "date-fns";
import { pt } from "react-date-range/src/locale";
import { useIntl } from "react-intl";

type Props = {
  initialDate: Date | null;
  endDate: Date | null;
  minDate?: Date;
  onApplyDates: (startDate: Date | null, endDate: Date | null) => void;
};

const DateRangeButton: FC<Props> = ({ onApplyDates, initialDate, endDate, minDate }) => {
  const intl = useIntl();

  const [selectionRange, setSelectionRange] = useState({
    startDate: initialDate ?? undefined,
    endDate: endDate ?? undefined,
    key: "selection",
  });

  useEffect(() => {
    setSelectionRange({
      startDate: initialDate ?? undefined,
      endDate: endDate ?? undefined,
      key: "selection",
    });
  }, [initialDate, endDate]);

  const defineds = {
    startOfWeek: startOfWeek(new Date()),
    endOfWeek: endOfWeek(new Date()),
    startOfLastWeek: startOfWeek(addDays(new Date(), -7)),
    endOfLastWeek: endOfWeek(addDays(new Date(), -7)),
    startOfToday: startOfDay(new Date()),
    endOfToday: endOfDay(new Date()),
    startOfYesterday: startOfDay(addDays(new Date(), -1)),
    endOfYesterday: endOfDay(addDays(new Date(), -1)),
    startOfMonth: startOfMonth(new Date()),
    endOfMonth: endOfMonth(new Date()),
    startOfLastMonth: startOfMonth(addMonths(new Date(), -1)),
    endOfLastMonth: endOfMonth(addMonths(new Date(), -1)),
    startOf3MonthsAgo: startOfMonth(addMonths(new Date(), -3)),
    startOf6MonthsAgo: startOfMonth(addMonths(new Date(), -6)),
    startOf12MonthsAgo: startOfMonth(addMonths(new Date(), -12)),
  };

  const clampStart = (d: Date) => (minDate && d < minDate ? minDate : d);

  const staticRanges = createStaticRanges([
    {
      label: intl.formatMessage({
        id: "page.nps.ranges-today",
        defaultMessage: "Hoje",
      }),
      range: () => ({
        startDate: defineds.startOfToday,
        endDate: defineds.endOfToday,
      }),
    },
    {
      label: intl.formatMessage({
        id: "page.nps.ranges-yesterday",
        defaultMessage: "Ontem",
      }),
      range: () => ({
        startDate: defineds.startOfYesterday,
        endDate: defineds.endOfYesterday,
      }),
    },
    {
      label: intl.formatMessage({
        id: "page.nps.ranges-this-week",
        defaultMessage: "Essa semana",
      }),
      range: () => ({
        startDate: defineds.startOfWeek,
        endDate: defineds.endOfWeek,
      }),
    },
    {
      label: intl.formatMessage({
        id: "page.nps.ranges-last-week",
        defaultMessage: "Semana passada",
      }),
      range: () => ({
        startDate: defineds.startOfLastWeek,
        endDate: defineds.endOfLastWeek,
      }),
    },
    {
      label: intl.formatMessage({
        id: "page.nps.ranges-this-month",
        defaultMessage: "Esse mês",
      }),
      range: () => ({
        startDate: defineds.startOfMonth,
        endDate: defineds.endOfMonth,
      }),
    },
    {
      label: intl.formatMessage({
        id: "page.nps.ranges-last-month",
        defaultMessage: "Mês passado",
      }),
      range: () => ({
        startDate: defineds.startOfLastMonth,
        endDate: defineds.endOfLastMonth,
      }),
    },
    {
      label: intl.formatMessage({
        id: "page.nps.ranges-last-3-months",
        defaultMessage: "Últimos 3 meses",
      }),
      range: () => ({
        startDate: clampStart(defineds.startOf3MonthsAgo),
        endDate: defineds.endOfToday,
      }),
    },
    {
      label: intl.formatMessage({
        id: "page.nps.ranges-last-6-months",
        defaultMessage: "Últimos 6 meses",
      }),
      range: () => ({
        startDate: clampStart(defineds.startOf6MonthsAgo),
        endDate: defineds.endOfToday,
      }),
    },
    {
      label: intl.formatMessage({
        id: "page.nps.ranges-last-12-months",
        defaultMessage: "Últimos 12 meses",
      }),
      range: () => ({
        startDate: clampStart(defineds.startOf12MonthsAgo),
        endDate: defineds.endOfToday,
      }),
    },
  ]);

  const handleDateChange = (evt: any) => {
    setSelectionRange({
      startDate: evt.selection.startDate,
      endDate: evt.selection.endDate,
      key: "selection",
    });
  };

  const clearDates = () => {
    setSelectionRange({
      startDate: undefined,
      endDate: undefined,
      key: "selection",
    });
    onApplyDates(null, null);
  };

  const applyDates = () => {
    onApplyDates(selectionRange.startDate!, selectionRange.endDate!);
  };

  const hasFilterApplied = initialDate && endDate;

  return (
    <div className="d-flex my-2 mx-lg-4">
      <div
        className="btn btn-light fs-7 py-3"
        data-kt-menu-trigger="click"
        data-kt-menu-placement="bottom-start"
        data-kt-menu-flip="top-end"
        data-kt-menu-attach="parent"
      >
        <i className="bi bi-calendar" />
        {hasFilterApplied
          ? `${format(initialDate!, "dd/MM/yyyy")} a ${format(
              endDate!,
              "dd/MM/yyyy"
            )}`
          : intl.formatMessage({
              id: "page.nps-responses.filter-by-date",
              defaultMessage: "Filtrar por data",
            })}
      </div>

      <div className="menu menu-sub menu-sub-dropdown" data-kt-menu="true">
        <DateRangePicker
          showDateDisplay={false}
          ranges={[{
            startDate: selectionRange.startDate ?? new Date(),
            endDate: selectionRange.endDate ?? new Date(),
            key: "selection",
          }]}
          onChange={handleDateChange}
          inputRanges={[]}
          staticRanges={staticRanges}
          locale={pt}
          minDate={minDate}
        />
        <div className="text-end">
          <button
            className="btn btn-light mb-5 me-5"
            data-kt-menu-dismiss={true}
            onClick={clearDates}
          >
            {intl.formatMessage({
              id: "page.nps-responses.btn-clean",
              defaultMessage: "Limpar",
            })}
          </button>
          <button
            className="btn btn-primary mb-5 me-5"
            data-kt-menu-dismiss={true}
            onClick={applyDates}
          >
            {intl.formatMessage({
              id: "page.nps-responses.btn-apply",
              defaultMessage: "Aplicar",
            })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateRangeButton;
